// src/pages/EmailTemplates.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../utils/api';

const TEMPLATES = [
  {
    id: 'file-share',
    label: '📁 File Share Notification',
    description: 'Sent to recipients when a file transfer is created or a new recipient is added.',
    placeholders: [
      { key: '{{uploaderName}}', desc: 'Name of the person who created the transfer' },
      { key: '{{title}}', desc: 'Transfer title' },
      { key: '{{expiry}}', desc: 'Expiry date (formatted)' },
      { key: '{{fileList}}', desc: 'List of file names' },
      { key: '{{viewOnly}}', desc: 'Access type (View only / Download)' },
    ],
    defaultSubject: 'Secure files shared with you: {{title}}',
  },
  {
    id: 'credential-share',
    label: '🔐 Credential Share Notification',
    description: 'Sent to recipients when a credential transfer is created.',
    placeholders: [
      { key: '{{uploaderName}}', desc: 'Name of the person who created the transfer' },
      { key: '{{title}}', desc: 'Transfer title / description' },
      { key: '{{expiry}}', desc: 'Expiry date (formatted)' },
    ],
    defaultSubject: 'Secure credentials shared with you: {{title}}',
  },
  {
    id: 'file-request',
    label: '📥 File Request Notification',
    description: 'Sent to a third party when you create a file request — asking them to upload files to you.',
    placeholders: [
      { key: '{{senderName}}', desc: 'Your name (the person making the request)' },
      { key: '{{title}}', desc: 'Request title' },
      { key: '{{message}}', desc: 'Optional message to the recipient' },
      { key: '{{expiry}}', desc: 'Expiry date (formatted)' },
    ],
    defaultSubject: '{{senderName}} is requesting files from you — SUFC',
  },
  {
    id: 'credential-request',
    label: '🔓 Credential Request Notification',
    description: 'Sent to a third party when you create a credential request — asking them to submit credentials to you.',
    placeholders: [
      { key: '{{senderName}}', desc: 'Your name (the person making the request)' },
      { key: '{{title}}', desc: 'Request title' },
      { key: '{{message}}', desc: 'Optional message to the recipient' },
      { key: '{{expiry}}', desc: 'Expiry date (formatted)' },
    ],
    defaultSubject: '{{senderName}} is requesting credentials from you — SUFC',
  },
  {
    id: 'otp-code',
    label: '🔢 OTP Verification Code',
    description: 'Sent when a recipient requests their one-time access code.',
    placeholders: [
      { key: '{{code}}', desc: 'The 6-digit OTP code' },
      { key: '{{title}}', desc: 'Transfer title' },
      { key: '{{maskedEmail}}', desc: 'Recipient\'s masked email address' },
    ],
    defaultSubject: 'Your SUFC Secure Transfer System verification code',
  },
];

// Simple WYSIWYG toolbar buttons
const TOOLBAR = [
  { cmd: 'bold', icon: 'B', style: { fontWeight: 'bold' }, title: 'Bold' },
  { cmd: 'italic', icon: 'I', style: { fontStyle: 'italic' }, title: 'Italic' },
  { cmd: 'underline', icon: 'U', style: { textDecoration: 'underline' }, title: 'Underline' },
  { cmd: 'insertUnorderedList', icon: '≡', title: 'Bullet list' },
];

// Pre-built HTML blocks to insert
const EMAIL_BLOCKS = [
  {
    label: '📋 Transfer Summary',
    html: `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color:#f8f8f8;border:1px solid #e0e0e0;margin-bottom:8px;"><tr><td style="padding:16px;font-family:Arial,sans-serif;font-size:13px;color:#333333;"><p style="margin:0 0 6px 0;"><strong>Transfer:</strong> {{title}}</p><p style="margin:0 0 6px 0;"><strong>Files:</strong> {{fileList}}</p><p style="margin:0;"><strong>Expires:</strong> {{expiry}}</p></td></tr></table>`,
  },
  {
    label: '⚠️ Caution (amber)',
    html: `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:8px;"><tr><td height="12" style="font-size:0;line-height:0;">&nbsp;</td></tr><tr><td style="background-color:#fff3cd;border-left:4px solid #f59e0b;padding:12px 16px;font-family:Arial,sans-serif;font-size:13px;color:#333333;line-height:1.5;">⚠️ <strong>Important:</strong> Add your caution message here.</td></tr></table>`,
  },
  {
    label: '🚨 Warning (red)',
    html: `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:8px;"><tr><td height="12" style="font-size:0;line-height:0;">&nbsp;</td></tr><tr><td style="background-color:#fde8e8;border-left:4px solid #CE0034;padding:12px 16px;font-family:Arial,sans-serif;font-size:13px;color:#333333;line-height:1.5;">🚨 <strong>Warning:</strong> Add your warning message here.</td></tr></table>`,
  },
  {
    label: '🔑 Password notice',
    html: `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:8px;"><tr><td height="12" style="font-size:0;line-height:0;">&nbsp;</td></tr><tr><td style="background-color:#fde8e8;border-left:4px solid #CE0034;padding:12px 16px;font-family:Arial,sans-serif;font-size:13px;color:#333333;line-height:1.5;">🔑 <strong>A password is required.</strong> The sender will provide this separately — not by email.</td></tr></table>`,
  },
  {
    label: '🔒 OTP notice',
    html: `<p style="font-family:Arial,sans-serif;font-size:13px;color:#555555;">You will need to verify your identity with a one-time code sent to this email address before you can access the files.</p>`,
  },
];

function WysiwygEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInitialised = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInitialised.current) {
      editorRef.current.innerHTML = value || '';
      isInitialised.current = true;
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  };

  const insertPlaceholder = (placeholder) => {
    editorRef.current?.focus();
    document.execCommand('insertText', false, placeholder);
    onChange(editorRef.current?.innerHTML || '');
  };

  return { editorRef, exec, insertPlaceholder };
}


function PreviewFrame({ templateId, bodyHtml, subject }) {
  const [html, setHtml] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    apiFetch(`mgmt/email-templates/${templateId}/preview`, {
      method: 'POST',
      body: JSON.stringify({ bodyHtml, subject }),
    }).then(d => { if (!cancelled) { setHtml(d.html || ''); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [templateId, bodyHtml, subject]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (error) return <div className="alert alert-error" style={{ margin: '16px' }}>❌ {error}</div>;
  return (
    <iframe
      srcDoc={html}
      style={{ width: '100%', minHeight: '500px', border: 'none', background: 'white' }}
      title="Email preview"
      sandbox="allow-same-origin"
    />
  );
}

function TemplateEditor({ template, saved, onSaved }) {
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const editorRef = useRef(null);
  const isInit = useRef(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  // Set editor content via callback ref so it runs after mount
  const setEditorRef = React.useCallback((node) => {
    editorRef.current = node;
    if (node) {
      const body = saved?.body || '';
      node.innerHTML = body;
      setBodyHtml(body);
    }
  }, [saved]); // eslint-disable-line

  useEffect(() => {
    const subj = saved?.subject || template.defaultSubject;
    setSubject(subj);
    // Also update editor if already mounted
    if (editorRef.current) {
      const body = saved?.body || '';
      editorRef.current.innerHTML = body;
      setBodyHtml(body);
    }
  }, [saved, template]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    setBodyHtml(editorRef.current?.innerHTML || '');
  };

  const insertPlaceholder = (ph) => {
    editorRef.current?.focus();
    document.execCommand('insertText', false, ph);
    setBodyHtml(editorRef.current?.innerHTML || '');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(`mgmt/email-templates/${template.id}`, {
        method: 'PUT',
        body: JSON.stringify({ subject, bodyHtml: editorRef.current?.innerHTML || bodyHtml }),
      });
      onSaved();
      showToast('✅ Template saved');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default template? Your customisation will be lost.')) return;
    setSaving(true);
    try {
      await apiFetch(`mgmt/email-templates/${template.id}`, {
        method: 'PUT',
        body: JSON.stringify({ subject: template.defaultSubject, bodyHtml: '' }),
      });
      setSubject(template.defaultSubject);
      if (editorRef.current) editorRef.current.innerHTML = '';
      onSaved();
      showToast('✅ Reset to default');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) return showToast('Enter a test email address first');
    setTesting(true);
    try {
      const data = await apiFetch(`mgmt/email-templates/${template.id}/test`, {
        method: 'POST',
        body: JSON.stringify({ toEmail: testEmail.trim(), subject, bodyHtml: editorRef.current?.innerHTML || bodyHtml }),
      });
      showToast(`✅ Test email sent to ${data.sentTo}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setTesting(false); }
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: 'var(--g500)', marginBottom: '16px' }}>{template.description}</p>

      {saved?.updatedAt && (
        <div style={{ fontSize: '12px', color: 'var(--g400)', marginBottom: '16px' }}>
          Last saved by {saved.updatedBy} on {new Date(saved.updatedAt).toLocaleString('en-GB')}
        </div>
      )}

      {/* Subject */}
      <div className="form-group">
        <label className="form-label">Email subject</label>
        <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder={template.defaultSubject} />
      </div>

      {/* Placeholders */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--g400)', marginBottom: '6px' }}>Available placeholders — click to insert</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {template.placeholders.map(p => (
            <button key={p.key} title={p.desc}
              onClick={() => insertPlaceholder(p.key)}
              style={{ background: 'var(--g100)', border: '1px solid var(--g300)', padding: '3px 8px', fontSize: '12px', fontFamily: 'var(--font-mono)', cursor: 'pointer', color: 'var(--red)' }}>
              {p.key}
            </button>
          ))}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '6px' }}>These are replaced with real values when the email is sent. Only use placeholders from the list above.</div>
      </div>

      {/* Toolbar + Editor */}
      <div style={{ border: '1px solid var(--g300)', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '2px', padding: '6px', borderBottom: '1px solid var(--g200)', background: 'var(--g50)', flexWrap: 'wrap', alignItems: 'center' }}>
          {TOOLBAR.map(btn => (
            <button key={btn.cmd + (btn.value || '')} title={btn.title}
              onClick={() => exec(btn.cmd, btn.value || null)}
              style={{ ...btn.style, background: 'white', border: '1px solid var(--g300)', padding: '4px 10px', cursor: 'pointer', fontSize: '13px', minWidth: '32px' }}>
              {btn.icon}
            </button>
          ))}
          <div style={{ width: '1px', height: '24px', background: 'var(--g300)', margin: '0 4px' }} />
          <button title="Undo" onClick={() => exec('undo')} style={{ background: 'white', border: '1px solid var(--g300)', padding: '4px 10px', cursor: 'pointer', fontSize: '13px' }}>↩</button>
          <button title="Redo" onClick={() => exec('redo')} style={{ background: 'white', border: '1px solid var(--g300)', padding: '4px 10px', cursor: 'pointer', fontSize: '13px' }}>↪</button>
          <div style={{ width: '1px', height: '24px', background: 'var(--g300)', margin: '0 4px' }} />
          {EMAIL_BLOCKS.map(block => (
            <button key={block.label} title={`Insert: ${block.label}`}
              onClick={() => {
                editorRef.current?.focus();
                document.execCommand('insertHTML', false, block.html);
                setBodyHtml(editorRef.current?.innerHTML || '');
              }}
              style={{ background: 'var(--g50)', border: '1px solid var(--g300)', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}>
              {block.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button onClick={() => setPreviewMode(m => !m)}
            style={{ background: previewMode ? 'var(--red)' : 'white', color: previewMode ? 'white' : 'var(--g700)', border: '1px solid var(--g300)', padding: '4px 12px', cursor: 'pointer', fontSize: '12px' }}>
            {previewMode ? '✏️ Edit' : '👁 Preview'}
          </button>
        </div>

        {previewMode ? (
          <PreviewFrame templateId={template.id} bodyHtml={editorRef.current?.innerHTML || bodyHtml} subject={subject} />
        ) : (
          <div ref={setEditorRef} contentEditable suppressContentEditableWarning
            onInput={e => setBodyHtml(e.currentTarget.innerHTML)}
            style={{ padding: '16px', minHeight: '200px', outline: 'none', fontSize: '14px', lineHeight: '1.6', background: 'white' }} />
        )}
      </div>

      <div style={{ fontSize: '12px', color: 'var(--g400)', marginBottom: '16px', padding: '10px', background: 'var(--g50)', border: '1px solid var(--g200)' }}>
        <strong>Note:</strong> The email header (SUFC branding), button and footer are fixed and cannot be edited. Only the body content above is customisable. If left empty, the default template will be used.
      </div>

      {/* Test email */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', padding: '12px', background: 'var(--g50)', border: '1px solid var(--g200)' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--g600)', whiteSpace: 'nowrap' }}>Send test to:</div>
        <input className="form-input" style={{ flex: 1 }} type="email" placeholder="your@email.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleTest()} />
        <button className="btn btn-secondary btn-sm" onClick={handleTest} disabled={testing || !testEmail.trim()}>
          {testing ? 'Sending…' : '📧 Send test'}
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save template'}</button>
        <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>↩ Reset to default</button>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export default function EmailTemplates() {
  const [activeTemplate, setActiveTemplate] = useState(TEMPLATES[0].id);
  const [savedTemplates, setSavedTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('mgmt/email-templates');
      setSavedTemplates(data.templates || {});
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const currentTemplate = TEMPLATES.find(t => t.id === activeTemplate);

  return (
    <div>
      <div className="page-eyebrow">Administration</div>
      <h1 className="page-title">Email Templates</h1>
      <div className="mobile-only-msg">
        <div className="alert alert-warn" style={{ marginBottom: '16px' }}>
          📱 Email templates can't be edited on mobile. Please use the desktop site to customise email templates.
        </div>
      </div>
      <div className="desktop-only-content">
      <p className="page-subtitle">Customise the email notifications sent to recipients. Changes take effect immediately.</p>

      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>❌ {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '20px', alignItems: 'start' }}>
        {/* Template selector */}
        <div className="card" style={{ padding: 0 }}>
          {TEMPLATES.map(t => (
            <button key={t.id} onClick={() => setActiveTemplate(t.id)}
              style={{
                display: 'block', width: '100%', padding: '14px 16px', textAlign: 'left',
                background: activeTemplate === t.id ? 'var(--red)' : 'transparent',
                color: activeTemplate === t.id ? 'white' : 'var(--g700)',
                border: 'none', borderBottom: '1px solid var(--g100)', cursor: 'pointer',
                fontSize: '13px', fontWeight: activeTemplate === t.id ? 700 : 400,
              }}>
              {t.label}
              {savedTemplates[t.id]?.body && (
                <span style={{ display: 'block', fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>Customised</span>
              )}
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">{currentTemplate?.label}</span>
          </div>
          <div className="card-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : currentTemplate ? (
              <TemplateEditor
                key={activeTemplate}
                template={currentTemplate}
                saved={savedTemplates[activeTemplate] || null}
                onSaved={loadTemplates}
              />
            ) : null}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
